import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { notification } from 'tea-component';
import BoardGovernanceNew from '../BoardGovernanceNew';
import { getBoardGovernanceIntegrated } from '@sg/services/studio';
import { getButtonPermission } from '@sg/_config/permission';
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import { BoardGovernanceTab } from '@sg/utils/buriedPoint';

const BOARD_GOVERNANCE_PAGE_ID = 'studio_boardGovernance';

interface BoardGovernanceProps {
  /** Studio IDs from the top-bar multi-select filter (owned by parent). */
  selectedStudioIds: string[];
  /** Keep timeline in loading state while studio options are still fetching. */
  studioOptionsLoading?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
}

/**
 * Standalone Board Governance tab: fetches integrated meetings for the selected
 * studios, then renders the shared BoardGovernanceNew timeline/UI.
 */
const BoardGovernance: React.FC<BoardGovernanceProps> = ({
  selectedStudioIds,
  studioOptionsLoading = false,
  onEditingChange,
}) => {
  const { ready: permissionReady } = usePermissionReady();
  // null = not loaded yet; [] = loaded but empty (no meetings / no studios selected)
  const [data, setData] = useState<any[] | null>(null);
  const [datatime, setDatatime] = useState('');
  const [loading, setLoading] = useState(false);
  // Ignore stale responses when the user changes studio filter quickly.
  const latestRequestIdRef = useRef(0);

  // Recompute after permission model is ready (getButtonPermission reads live ACL).
  const hasEditPermission = useMemo(
    () => getButtonPermission(BOARD_GOVERNANCE_PAGE_ID),
    [permissionReady],
  );

  const fetchMeetings = useCallback(async (studioIds: string[]) => {
    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;

    // No selection → empty timeline; skip API so we don't send studio_ids: [].
    if (!studioIds.length) {
      setData([]);
      setDatatime('');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getBoardGovernanceIntegrated({
        studio_ids: studioIds,
      });
      // rawData: true — tolerate either { data: { data, datatime } } or unwrapped body.
      const responseData = response?.data ?? response;
      const rawData = responseData?.data || [];

      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      setDatatime(responseData?.datatime || '');

      // Sort years ascending so the timeline reads oldest → newest.
      const sortedData = [...rawData].sort(
        (a: any, b: any) => Number(a.year) - Number(b.year),
      );

      // BoardGovernanceNew expects: [{ year, quarters: [{ quarter, meetings }] }]
      setData(sortedData);
    } catch (error) {
      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      console.error('Failed to fetch Board Governance data:', error);
      notification.error({
        description: 'Failed to fetch board governance data',
      });
      setData([]);
      setDatatime('');
    } finally {
      // Only the latest request may clear loading (older ones already returned above).
      if (requestId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Sorted set key: drives meeting_id clear + meetings fetch (order-insensitive).
  const meetingUrlScopeKey = useMemo(
    () => [...selectedStudioIds].sort().join(','),
    [selectedStudioIds],
  );

  useEffect(() => {
    void fetchMeetings(selectedStudioIds);

    // Bump request id on unmount/deps change so in-flight replies can't write state.
    return () => {
      latestRequestIdRef.current += 1;
    };
  }, [fetchMeetings, meetingUrlScopeKey]);

  return (
    <BoardGovernanceNew
      data={data}
      datatime={datatime}
      // Hold UI until both meetings and studio-filter options are ready.
      loading={loading || studioOptionsLoading}
      onRefresh={() => fetchMeetings(selectedStudioIds)}
      hasEditPermission={hasEditPermission}
      buriedPointTab={BoardGovernanceTab}
      buriedPointExtInfo="Board Governance Edit"
      domain="mgmt/studio/board_governance"
      onEditingChange={onEditingChange}
      meetingUrlScopeKey={meetingUrlScopeKey}
    />
  );
};

export default BoardGovernance;
